import { AsYouType, getCountryCallingCode, isSupportedCountry } from "libphonenumber-js/max";
import { getUITranslation } from "../utils/get_ui_translation";

function restoreCursorPosition(el, formatter, formatFn) {
  const cursorPosition = el.selectionStart;
  const unformattedValue = el.value;

  // Trap early cursor modifications, avoids shuffling all the numbers for
  // changes at the beginning of the input
  if (el === document.activeElement && cursorPosition < 6) {
    return;
  }

  // Format
  el.value = formatFn(el.value);

  const beforeLeftOfCursorBeforeFormatting = unformattedValue.slice(0, cursorPosition);

  formatter.reset();
  const formatted = formatter.input(beforeLeftOfCursorBeforeFormatting);
  const newPosition = formatted.length;

  el.setSelectionRange(newPosition, newPosition);
}

const countryFromLocale = () => {
  const locale = window._locale;
  const edgeCases = {
    en: "UK",
  };

  return edgeCases[locale] || locale?.toUpperCase();
};

// libphonenumber only knows ISO 3166 codes, while the UK country version and the en locale use "UK"
const toCountry = (code) => (code === "UK" ? "GB" : code);

const invalidNumberMessage = (country) => {
  if (!isSupportedCountry(country)) return getUITranslation("Veuillez entrer un numéro de téléphone valide !");

  return getUITranslation(
    "Veuillez entrer un numéro de téléphone valide, en commençant par l'indicatif du pays (par exemple +%{calling_code}).",
    { calling_code: getCountryCallingCode(country) }
  );
};

const getCountry = (input, defaultCountry) => {
  if (defaultCountry === "FR") {
    // We're in France, let's check if we're handing a DROM-COM number
    // Note: we're only processing prefixes for territories that have numbers that _look like_
    // metropolitan numbers, to avoid them being parsed as such.
    const dromComPrefixes = {
      262: "RE", // La Réunion (landline)
      263: "RE", // La Réunion (landline cont.)
      692: "RE", // La Réunion (mobile)
      693: "RE", // La Réunion (mobile cont.)
      9762: "RE", // La Réunion (VOIP)
      9763: "RE", // La Réunion (VOIP cont.)
      9769: "RE", // La Réunion (VOIP cont.)

      269: "YT", // Mayotte (landline)
      639: "YT", // Mayotte (mobile)

      590: "GP", // Guadeloupe (landline)
      690: "GP", // Guadeloupe (mobile)
      691: "GP", // Guadeloupe (mobile cont.)
      9760: "GP", // Guadeloupe (VOIP)
      9761: "GP", // Guadeloupe (VOIP cont.)
      9768: "GP", // Guadeloupe (VOIP cont.)

      594: "GF", // Guyane (landline)
      694: "GF", // Guyane (mobile)
      9764: "GF", // Guyane (VOIP)
      9765: "GF", // Guyane (VOIP cont.)

      596: "MQ", // Martinique (landline)
      696: "MQ", // Martinique (mobile)
      697: "MQ", // Martinique (mobile cont.)
      9766: "MQ", // Martinique (VOIP)
      9767: "MQ", // Martinique (VOIP cont.)

      508: "PM", // Saint-Pierre-et-Miquelon
    };

    // Remove all non-numeric characters
    const value = input.replace(/[^\d]/g, "");

    const valuePrefix = value.startsWith("0") ? value.slice(1, 5) : value.slice(0, 4);
    for (const prefix of Object.keys(dromComPrefixes)) {
      if (valuePrefix.startsWith(prefix)) {
        return dromComPrefixes[prefix];
      }
    }
  }

  return defaultCountry;
};

const directive = (el, { expression }, { evaluateLater, effect, cleanup }) => {
  const evaluateCountry = evaluateLater(expression);
  let defaultCountry;

  const onInput = () => {
    const formatter = new AsYouType(getCountry(el.value, defaultCountry));

    // When an input element's value is set, it moves the cursor to the end
    // therefore we need to track, estimate, and restore the cursor after
    // a change was made.
    restoreCursorPosition(el, formatter, (value) => {
      // Clear validity on input so we don't get the message in our face while
      // we fix the validation
      el.setCustomValidity("");
      el.reportValidity();

      // Typing is more natural if we take into account spaces at the end
      const endsWithSpace = value.endsWith(" ");

      // Save if first character is a +
      const startsWithPlus = value.startsWith("+");
      // Remove all non-numeric characters and add the + back if needed
      value = (startsWithPlus ? "+" : "") + value.replace(/[^\d ]/g, "");

      formatter.reset();
      return formatter.input(value) + (endsWithSpace ? " " : "");
    });
  };

  // On blur, we check if it's valid, format if necessary
  const onBlur = () => {
    const formatter = new AsYouType(getCountry(el.value, defaultCountry));
    const formatted = formatter.input(el.value);
    if (formatter.isValid()) {
      // Make sure value is always formatted on blur (we might be coming from the < 6 trap)
      if (formatted.startsWith("+") || formatted.startsWith("0")) {
        el.value = formatted;
      } else {
        // We're in an edge case, e.g. 6060606060 (+33 6 06 06 06 06) is considered valid, we format it
        // in intl format to make sure it's not a mistake and to add spaces.
        // This value (national number without +33) often is autocompleted by browsers.
        el.value = formatter.getNumber().format("INTERNATIONAL");
      }
      el.setCustomValidity("");
    } else if (!el.required && el.value === "") {
      // If the input is not required we don't want to report an invalid input
      el.setCustomValidity("");
    } else {
      el.setCustomValidity(invalidNumberMessage(defaultCountry));
    }
  };

  effect(() =>
    evaluateCountry((attr) => {
      const isCountryChange = defaultCountry !== undefined;
      defaultCountry = toCountry(typeof attr === "string" ? attr.toUpperCase() : countryFromLocale() || "FR");
      if (isCountryChange && el.value) onBlur();
    })
  );

  el.addEventListener("input", onInput);
  el.addEventListener("blur", onBlur);

  if (el.value) onInput();

  cleanup(() => {
    el.removeEventListener("input", onInput);
    el.removeEventListener("blur", onBlur);
  });
};

export default (Alpine) => {
  Alpine.directive("phonenumber", directive).before("model");
};
